import type { Placement, PlacementPosition } from '@repo/api-contracts';
import { AlignCenterHorizontal, AlignCenterVertical } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { IconToolButton } from '@/shared/ui';

interface PlacementInspectorProps {
  readonly placement: Placement;
  /** 이 배치를 표면 정중앙에 둘 위치. 인쇄 영역이나 표면을 찾을 수 없어 계산할 수 없으면 null 이고, 가운데 버튼은 꺼진다. */
  readonly centeredPosition: PlacementPosition | null;
  /** 적용되면 null, 거부되면 그 사유. 사유는 버튼 줄 아래에 그대로 보여준다. */
  readonly onApply: (placement: Placement) => string | null;
  readonly disabled: boolean;
}

/**
 * 선택한 인쇄 영역의 이 면 배치 정렬 — 가로 가운데·세로 가운데를 누르면 그 축만 표면 가운데로 바로 옮긴다.
 * 세밀한 위치는 사진 위에서 끌거나 화살표 키로 옮긴다(수치 칸은 두지 않는다).
 */
export const PlacementInspector = ({
  placement,
  centeredPosition,
  onApply,
  disabled,
}: PlacementInspectorProps): ReactElement => {
  const [error, setError] = useState<string | null>(null);
  const applyCentered = (axis: 'x' | 'y'): void => {
    if (disabled || centeredPosition === null) return;
    const position: PlacementPosition =
      axis === 'x'
        ? { xMm: centeredPosition.xMm, yMm: placement.position.yMm }
        : { xMm: placement.position.xMm, yMm: centeredPosition.yMm };
    setError(onApply({ ...placement, position }));
  };
  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="text-sm font-medium">정렬</legend>
      <div className="flex flex-wrap gap-2">
        <IconToolButton
          icon={AlignCenterVertical}
          label="가로 가운데"
          disabled={centeredPosition === null}
          onClick={() => applyCentered('x')}
        />
        <IconToolButton
          icon={AlignCenterHorizontal}
          label="세로 가운데"
          disabled={centeredPosition === null}
          onClick={() => applyCentered('y')}
        />
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
};
